import { useEffect, useRef, useState } from 'react';
import { Form, useActionData, useNavigation } from '@remix-run/react';
import { cn } from '~/lib/utils';
import landingType from '~/landing_page/styles/landingTypography.module.css';
import styles from './WaitlistLanding.module.css';

/**
 * The waitlist landing: the WhoItsForSection globe in its wireframe look — dashed empty frames
 * instead of covers, so the wall reads as a blueprint of what is coming — with the section's copy
 * kept and the CTA pair swapped for an email capture. Duplicated from WhoItsForSection rather than
 * shared, so the real landing section keeps drifting on its own.
 */

const ROLES = [
  'Founders pitching their next round',
  'PMs aligning the team on what’s next',
  'Designers making their case',
  'Engineers explaining the hard stuff',
  'Consultants on a client deadline',
] as const;

const ROLE_HOLD_MS = 2400;

/**
 * Same shell as WhoItsForSection: the eye sits at perspective 1100 just outside a 900 radius
 * sphere whose cards all face the middle, so the near half is backface-culled and what remains is
 * the concave far wall. Cards are tiled in latitude rows, odd rows offset half a column so the
 * tiling never reads as a grid.
 */
const SPHERE_RADIUS_PX = 900;
const CARD_W = 260;
const CARD_H = 146;
const CARD_GAP = 55;
const DEG = 180 / Math.PI;

const SPHERE_CARDS: string[] = [];
{
  const latStep = ((CARD_H + CARD_GAP) / SPHERE_RADIUS_PX) * DEG;
  for (let lat = -84; lat <= 84; lat += latStep) {
    const ringRadius = Math.cos(lat / DEG) * SPHERE_RADIUS_PX;
    const columns = Math.max(1, Math.round((2 * Math.PI * ringRadius) / (CARD_W + CARD_GAP)));
    for (let c = 0; c < columns; c += 1) {
      const yaw = (c / columns) * 360 + (Math.round(lat) % 2 === 0 ? 0 : 180 / columns);
      SPHERE_CARDS.push(
        `rotateY(${yaw.toFixed(2)}deg) rotateX(${(-lat).toFixed(2)}deg) ` +
          `translateZ(${SPHERE_RADIUS_PX}px) rotateY(180deg)`,
      );
    }
  }
}

export type WaitlistActionData = { ok: true } | { ok: false; error: string };

export interface WaitlistLandingProps {
  className?: string;
  headline?: string;
  roles?: readonly string[];
}

export function WaitlistLanding({
  className,
  headline = 'Storytelling is everybody’s job.',
  roles = ROLES,
}: WaitlistLandingProps) {
  const actionData = useActionData<WaitlistActionData>();
  const navigation = useNavigation();
  const submitting = navigation.state === 'submitting';

  // Same roll as WhoItsForSection: the first role repeats at the end so the step from last to
  // first travels the same direction, then snaps back with the transition off for one frame.
  const [index, setIndex] = useState(0);
  const [snapping, setSnapping] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (roles.length <= 1) return;

    const clear = () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = null;
    };

    if (snapping) {
      timerRef.current = setTimeout(() => setSnapping(false), 20);
      return clear;
    }

    if (index === roles.length) {
      setIndex(0);
      setSnapping(true);
      return clear;
    }

    timerRef.current = setTimeout(() => setIndex((i) => i + 1), ROLE_HOLD_MS);
    return clear;
  }, [index, snapping, roles.length]);

  const rolled = [...roles, roles[0]];
  const joined = actionData?.ok === true;

  return (
    <main className={cn(styles.page, className)} data-name="WaitlistLanding">
      <header className={styles.header}>
        <a className={styles.logoLink} href="/" aria-label="Deck home">
          <img src="/landing/logo/deck-symbol.svg" alt="" width={24} height={22} />
        </a>
      </header>

      <div className={styles.sphereViewport} aria-hidden="true">
        <div className={styles.sphere}>
          {SPHERE_CARDS.map((transform, i) => (
            <div key={i} className={styles.sphereCard} style={{ transform }} />
          ))}
        </div>
      </div>

      <div className={styles.copyScrim} aria-hidden="true" />

      <div className={styles.stage}>
        <div className={cn(landingType.sectionInset, styles.container)}>
          <h1 className={cn(landingType.sectionHeadline, styles.headline)}>{headline}</h1>

          <div className={styles.roleViewport}>
            <div
              className={cn(styles.roleTrack, snapping && styles.roleTrackSnapping)}
              style={{ transform: `translateY(calc(${-index} * 100% / ${rolled.length}))` }}
            >
              {rolled.map((role, i) => (
                <p key={`${role}-${i}`} className={styles.role} aria-hidden="true">
                  {role}
                </p>
              ))}
            </div>
            <p className={styles.roleVisuallyHidden}>{roles[0]}</p>
          </div>

          {joined ? (
            <p className={styles.success}>You’re on the list. We’ll email you when Deck opens up.</p>
          ) : (
            <Form method="post" className={styles.form} replace>
              {/* Honeypot: humans never see it, form bots fill it. The action treats a filled
                  value as success without writing anything. */}
              <input
                className={styles.honeypot}
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <input
                className={styles.emailInput}
                type="email"
                name="email"
                required
                maxLength={320}
                placeholder="Your email"
                aria-label="Email address"
                autoComplete="email"
              />
              <button className={styles.submit} type="submit" disabled={submitting}>
                {submitting ? 'Adding you…' : 'Keep me posted'}
              </button>
            </Form>
          )}

          {!joined && actionData && actionData.ok === false ? (
            <p className={styles.error}>{actionData.error}</p>
          ) : null}
        </div>
      </div>
    </main>
  );
}

WaitlistLanding.displayName = 'WaitlistLanding';
